import { useEffect, useMemo, useState } from 'react';
import { pluralize } from '@shared/format';
import type { Path, Point } from '@shared/model/dataset';
import { useDataset } from '../hooks/useDataset';
import type { ItinerariesState } from '../hooks/useItineraries';
import { Alert } from './Alert';
import { PathCard } from './PathCard';
import styles from './Results.module.css';

export const RESULTS_PER_PAGE = 3;
const SAME_START_END_MESSAGE = "Pas besoin de partir, les points de départ et d'arrivée sont identiques :-)";
const NO_RESULTS_MESSAGE = 'Aucun itinéraire trouvé';
const NO_RESULTS_CLOSED_PATHS_MESSAGE = 'Aucun itinéraire trouvé. Tous les sentiers sont fermés.';

interface Props {
    state: ItinerariesState;
    start: Point | undefined;
    end: Point | undefined;
    hideClosed: boolean;
    activeIds: ReadonlySet<string>;
    onToggle: (path: Path) => void;
    onClear: () => void;
    /** Called with the first path of a new result set (auto expanded, as on the historical site). */
    onNewResults: (first: Path | undefined) => void;
}

export function Results({ state, start, end, hideClosed, activeIds, onToggle, onClear, onNewResults }: Props) {
    const { profiles, profilesVersion, loadProfiles } = useDataset();
    const [page, setPage] = useState(1);

    const allPaths = useMemo(() => (state.status === 'done' ? state.paths : []), [state]);
    const filtered = useMemo(
        () => (hideClosed ? allPaths.filter((p) => !p.hasClosed) : allPaths),
        [allPaths, hideClosed],
    );
    const pageCount = Math.max(1, Math.ceil(filtered.length / RESULTS_PER_PAGE));
    const currentPage = Math.min(page, pageCount);
    const pagePaths = useMemo(
        () => filtered.slice((currentPage - 1) * RESULTS_PER_PAGE, currentPage * RESULTS_PER_PAGE),
        [filtered, currentPage],
    );

    // New result set: back to page 1 (state adjusted during render, see react.dev) and expand the first itinerary.
    const [seenState, setSeenState] = useState(state);
    if (seenState !== state) {
        setSeenState(state);
        setPage(1);
    }
    useEffect(() => {
        if (state.status === 'done') onNewResults(filtered[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    // Elevation profiles of the itineraries shown.
    useEffect(() => {
        const ids = new Set(pagePaths.flatMap((p) => p.legs.map((l) => l.route.id)));
        if (ids.size > 0) void loadProfiles(ids).catch((e: unknown) => console.error(e));
    }, [pagePaths, loadProfiles]);

    // Keyboard shortcuts of the historical site.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement | null;
            if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable))
                return;
            if (e.key === 'Escape') onClear();
            else if (e.key === 'ArrowLeft') setPage((p) => Math.max(1, p - 1));
            else if (e.key === 'ArrowRight') setPage((p) => Math.min(pageCount, p + 1));
            else if (e.key === 'l') setPage(pageCount);
            else if (e.key === 'f') setPage(1);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClear, pageCount]);

    if (state.status === 'idle') return null;

    if (state.status === 'computing') {
        return (
            <Alert id="computation-progress" level={state.found > 0 ? 'warning' : 'info'} spinner>
                Calcul en cours…
                {state.found > 0 ? ` (${state.found} itinéraires trouvés)` : ''}
            </Alert>
        );
    }
    if (state.status === 'error') {
        return <Alert level="danger">Erreur pendant le calcul : {state.message}</Alert>;
    }
    if (!start || !end) return null;

    const title = `${start.name} → ${end.name}`;
    let body;
    if (start === end) {
        body = <Alert>{SAME_START_END_MESSAGE}</Alert>;
    } else if (filtered.length === 0) {
        body = (
            <Alert>{allPaths.length > 0 && hideClosed ? NO_RESULTS_CLOSED_PATHS_MESSAGE : NO_RESULTS_MESSAGE}</Alert>
        );
    } else {
        const maxDistance = Math.max(...pagePaths.map((p) => p.distance));
        const minAlt = Math.min(...pagePaths.map((p) => p.minAlt));
        const maxAlt = Math.max(...pagePaths.map((p) => p.maxAlt));
        body = (
            <>
                <div className={styles.summary}>
                    <b id="results-count">{pluralize(filtered.length, 'itinéraire')}</b>
                    <div className={styles.pager} id="page-count">
                        <button
                            type="button"
                            className={styles.pageButton}
                            onClick={() => setPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage <= 1}
                            aria-label="Page précédente"
                        >
                            &lt;
                        </button>
                        page {currentPage}/{pageCount}
                        <button
                            type="button"
                            className={styles.pageButton}
                            onClick={() => setPage(Math.min(pageCount, currentPage + 1))}
                            disabled={currentPage >= pageCount}
                            aria-label="Page suivante"
                        >
                            &gt;
                        </button>
                    </div>
                </div>
                <div id="results-group">
                    {pagePaths.map((path) => (
                        <PathCard
                            key={path.id}
                            path={path}
                            active={activeIds.has(path.id)}
                            onToggle={onToggle}
                            profiles={profiles}
                            profilesVersion={profilesVersion}
                            maxDistance={maxDistance}
                            minAlt={minAlt}
                            maxAlt={maxAlt}
                        />
                    ))}
                </div>
            </>
        );
    }

    return (
        <section id="list-results" aria-live="polite">
            <h4 className={`title-bar ${styles.title}`}>{title}</h4>
            {body}
        </section>
    );
}
